import type { EmployeeStore } from 'Stores/employeeStore/employeeStore'
import type { OrganisationStore } from 'Stores/organisationStore/organisationStore'

import type {
  getEmployee_employee_office_address,
  getEmployee_employee_organisation_contact_address,
} from 'Constants/graphql/queries/employee/__generated__/getEmployee'
import type { getOrganisation_organisation_contact_address } from 'Constants/graphql/queries/organisation/__generated__/getOrganisation'

import type { OrderDevice } from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'

import type { OrderLimitations } from './orderStore.types'
import { adjustChosenAccessoryLimitationRules } from './orderStoreLimitationRules.utils'

export const getOrderAddress = (
  isOrganisation: boolean,
  isEmployee: boolean,
  organisation: OrganisationStore['organisation'],
  employee: EmployeeStore['employee'],
):
  | getOrganisation_organisation_contact_address
  | getEmployee_employee_office_address
  | getEmployee_employee_organisation_contact_address
  | null
  | undefined => {
  const organisationAddress = organisation?.contact?.address
  const employeeAddress =
    employee?.office?.address || employee?.organisation?.contact?.address

  return isOrganisation && !isEmployee ? organisationAddress : employeeAddress
}

type CountTotalPriceReturnType = {
  totalBasis: number
  totalCalculatedNet: number
  totalWithTax: number
  totalWithoutTax: number
  totalPurchase: number
  totalPurchaseCopaymentWithTax: number
  totalPurchaseCopaymentWithoutTax: number
} | null

export const countTotalPrice = (
  chosenAccessories: Map<string, OrderDevice>,
  chosenDevice: OrderDevice | null,
  previewColumnDevice: OrderDevice | null,
): CountTotalPriceReturnType => {
  if (!chosenDevice && !previewColumnDevice) {
    return null
  }

  // [BLOCK] Primary device displayed prices counting logic
  const device = chosenDevice || previewColumnDevice

  const quantity = device?.quantity ?? 1
  const primaryDeviceCopaymentNet = device?.isPurchase
    ? 0
    : (device?.copaymentNet ?? 0)
  const primaryDeviceCalculatedNet = device?.isPurchase
    ? 0
    : (device?.calculatedNet ?? 0)
  const primaryCopaymentGross = device?.isPurchase
    ? 0
    : (device?.copaymentGross ?? 0)
  const primaryBasisAmount = device?.isPurchase ? 0 : (device?.basisAmount ?? 0)

  const primaryDevicePurchaseCopaymentNet = device?.isPurchase
    ? (device?.copaymentNet ?? 0)
    : 0
  const primaryDevicePurchaseCopaymentGross = device?.isPurchase
    ? (device?.copaymentGross ?? 0)
    : 0

  const addonDevicesCopaymentNet =
    device?.addonDevices?.reduce(
      (finalCopaymentNet, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalCopaymentNet
          : finalCopaymentNet + currentAddonDevice.copaymentNet,
      0,
    ) ?? 0

  const addonDevicesCopaymentGross =
    device?.addonDevices?.reduce(
      (finalCopaymentGross, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalCopaymentGross
          : finalCopaymentGross + currentAddonDevice.copaymentGross,
      0,
    ) ?? 0

  const addonDevicesPurchaseCopaymentNet =
    device?.addonDevices?.reduce(
      (finalCopaymentNet, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalCopaymentNet + currentAddonDevice.copaymentNet
          : finalCopaymentNet,
      0,
    ) ?? 0

  const addonDevicesPurchaseCopaymentGross =
    device?.addonDevices?.reduce(
      (finalCopaymentGross, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalCopaymentGross + currentAddonDevice.copaymentGross
          : finalCopaymentGross,
      0,
    ) ?? 0

  const addonDevicesBasisAmount =
    device?.addonDevices?.reduce(
      (finalBasisAmount, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalBasisAmount
          : finalBasisAmount + currentAddonDevice.basisAmount,
      0,
    ) ?? 0

  const addonDevicesCalculatedNet =
    device?.addonDevices?.reduce(
      (finalCalculatedNet, currentAddonDevice) =>
        currentAddonDevice.isPurchase
          ? finalCalculatedNet
          : finalCalculatedNet + (currentAddonDevice.calculatedNet ?? 0),
      0,
    ) ?? 0

  const copaymentNet = primaryDeviceCopaymentNet + addonDevicesCopaymentNet
  const copaymentGross = primaryCopaymentGross + addonDevicesCopaymentGross
  const basisAmount = primaryBasisAmount + addonDevicesBasisAmount
  const calculatedNet = primaryDeviceCalculatedNet + addonDevicesCalculatedNet
  const purchaseCopaymentNet =
    primaryDevicePurchaseCopaymentNet + addonDevicesPurchaseCopaymentNet
  const purchaseCopaymentGross =
    primaryDevicePurchaseCopaymentGross + addonDevicesPurchaseCopaymentGross

  let totalWithoutTax = copaymentNet * quantity
  let totalWithTax = copaymentGross * quantity
  let totalCalculatedNet = calculatedNet * quantity
  let totalBasis = basisAmount * quantity
  let totalPurchase = (device?.bundlePurchaseAmount ?? 0) * quantity
  let totalPurchaseCopaymentWithoutTax = purchaseCopaymentNet * quantity
  let totalPurchaseCopaymentWithTax = purchaseCopaymentGross * quantity

  // [BLOCK] Additional, non-mandatory addons displayed prices counting logic
  for (const [_, accessory] of chosenAccessories) {
    if (!accessory.isPurchase && accessory.copaymentGross) {
      totalWithTax += accessory.copaymentGross * accessory.quantity
    }

    if (!accessory.isPurchase && accessory.copaymentNet) {
      totalWithoutTax += accessory.copaymentNet * accessory.quantity
    }

    if (!accessory.isPurchase && accessory.calculatedNet) {
      totalCalculatedNet += accessory.calculatedNet * accessory.quantity
    }

    if (!accessory.isPurchase && accessory.basisAmount) {
      totalBasis += accessory.basisAmount * accessory.quantity
    }

    if (accessory.isPurchase && accessory.basisAmount) {
      totalPurchase += accessory.basisAmount * accessory.quantity
    }

    if (accessory.isPurchase && accessory.copaymentNet) {
      totalPurchaseCopaymentWithoutTax +=
        accessory.copaymentNet * accessory.quantity
    }

    if (accessory.isPurchase && accessory.copaymentGross) {
      totalPurchaseCopaymentWithTax +=
        accessory.copaymentGross * accessory.quantity
    }
  }

  return {
    totalBasis,
    totalCalculatedNet,
    totalPurchase,
    totalPurchaseCopaymentWithTax,
    totalPurchaseCopaymentWithoutTax,
    totalWithTax,
    totalWithoutTax,
  }
}

export const skipAccessories = (
  accessories: OrderDevice[] | null,
  limitationRules: OrderLimitations | null,
  isProductOffer: boolean,
) => {
  if (
    isProductOffer &&
    accessories?.some(accessory => !accessory?.isCreateSeparateRental)
  ) {
    return false
  }

  if (isProductOffer && accessories?.some(accessory => accessory?.isPurchase)) {
    return false
  }

  return (
    !accessories?.length ||
    (limitationRules && limitationRules.other.initial <= 0)
  )
}

export const toggleAccessory = (
  accessory: OrderDevice,
  chosenAccessories: Map<string, OrderDevice>,
  isProductOffer: boolean,
  limitationRules: OrderLimitations | null,
  setLimitationRules: (limitations: OrderLimitations | null) => void,
) => {
  if (chosenAccessories.has(accessory.id)) {
    chosenAccessories.delete(accessory.id)

    if (isProductOffer && !accessory.isCreateSeparateRental) {
      return
    }

    if (isProductOffer && accessory.isPurchase) {
      return
    }

    const newLimitationRules = adjustChosenAccessoryLimitationRules(
      limitationRules,
      accessory,
      'add',
    )

    if (newLimitationRules) {
      setLimitationRules(newLimitationRules)
    }
  } else {
    chosenAccessories.set(accessory.id, accessory)

    if (isProductOffer && !accessory.isCreateSeparateRental) {
      return
    }

    if (isProductOffer && accessory.isPurchase) {
      return
    }

    const newLimitationRules = adjustChosenAccessoryLimitationRules(
      limitationRules,
      accessory,
      'remove',
    )

    if (newLimitationRules) {
      setLimitationRules(newLimitationRules)
    }
  }
}
